import React, { useState, useEffect } from 'react';
import oktaApiClient from '../okta/frontend/oktaApiClient';
import AddDevicesToGroupModal from './AddDevicesToGroupModal';

export default function SpeedTestGroupForm({ group, onClose, onSaved }) {
  const isEditing = Boolean(group);
  const [groupName, setGroupName] = useState(group?.group_name || '');
  const [description, setDescription] = useState(group?.description || '');
  const [groupType, setGroupType] = useState(group?.group_type || 'manual');
  const [selectedDevices, setSelectedDevices] = useState([]); // for new groups
  const [showDevicePicker, setShowDevicePicker] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async () => {
    if (!groupName.trim()) {
      setError('Group name is required.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      if (isEditing) {
        await oktaApiClient.put(`/speed-test-groups/${group.group_id}`, {
          group_name: groupName.trim(),
          description,
        });
      } else {
        // Create group then add devices in one shot
        const res = await oktaApiClient.post('/speed-test-groups', {
          group_name: groupName.trim(),
          description,
          group_type: groupType,
        });
        const newGroupId = res.data.group_id;
        if (selectedDevices.length > 0) {
          await oktaApiClient.post(`/speed-test-groups/${newGroupId}/devices`, {
            androidIds: selectedDevices.map(d => d.android_id),
          });
        }
      }
      onSaved();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save group.');
    } finally {
      setSaving(false);
    }
  };

  const handleDevicesChosen = (devices) => {
    setSelectedDevices(prev => {
      const existing = new Set(prev.map(d => d.android_id));
      const added = devices.filter(d => !existing.has(d.android_id));
      return [...prev, ...added];
    });
    setShowDevicePicker(false);
  };

  const removeSelectedDevice = (androidId) => {
    setSelectedDevices(prev => prev.filter(d => d.android_id !== androidId));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-lg font-semibold text-gray-900">
            {isEditing ? 'Edit Group' : 'Create New Group'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">✕</button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4">
          {error && (
            <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-red-700 text-sm">
              {error}
            </div>
          )}

          {/* Group name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Group Name</label>
            <input
              type="text"
              value={groupName}
              onChange={e => setGroupName(e.target.value)}
              placeholder="e.g. Yard 5 Flagged Devices"
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              autoFocus
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description <span className="text-gray-400 font-normal">(optional)</span></label>
            <input
              type="text"
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Short description..."
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Test type -- only shown when creating */}
          {!isEditing && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Test Type</label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="groupType"
                    value="manual"
                    checked={groupType === 'manual'}
                    onChange={() => setGroupType('manual')}
                  />
                  <span className="text-sm text-gray-700">Manual</span>
                  <span className="text-xs text-gray-400">— run on demand</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="groupType"
                    value="automated"
                    checked={groupType === 'automated'}
                    onChange={() => setGroupType('automated')}
                  />
                  <span className="text-sm text-gray-700">Automated</span>
                  <span className="text-xs text-gray-400">— every hour</span>
                </label>
              </div>
            </div>
          )}

          {/* Devices (only when creating) */}
          {!isEditing && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-gray-700">
                  Devices <span className="text-gray-400 font-normal">({selectedDevices.length} / 30)</span>
                </label>
                <button
                  type="button"
                  onClick={() => setShowDevicePicker(true)}
                  disabled={selectedDevices.length >= 30}
                  className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-40 transition-colors"
                >
                  + Add Devices
                </button>
              </div>
              {selectedDevices.length === 0 ? (
                <p className="text-xs text-gray-400 italic">No devices added yet.</p>
              ) : (
                <div className="max-h-36 overflow-y-auto space-y-1">
                  {selectedDevices.map(d => (
                    <div key={d.android_id} className="flex items-center justify-between px-3 py-1.5 bg-gray-50 rounded border border-gray-200 text-xs">
                      <span className="font-mono text-gray-700">{d.android_id}</span>
                      {d.yard_name && <span className="text-gray-400 mx-2">{d.yard_name}</span>}
                      <button
                        onClick={() => removeSelectedDevice(d.android_id)}
                        className="text-red-400 hover:text-red-600 ml-auto"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t bg-gray-50 rounded-b-lg">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 transition-colors">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            {saving ? 'Saving...' : (isEditing ? 'Save Changes' : 'Create Group')}
          </button>
        </div>
      </div>

      {/* Device picker opened inside the form */}
      {showDevicePicker && (
        <AddDevicesToGroupModal
          group={{ group_id: '__new__', group_name: groupName || 'New Group' }}
          existingMembers={selectedDevices}
          maxAdd={30 - selectedDevices.length}
          onClose={() => setShowDevicePicker(false)}
          onAdded={handleDevicesChosen}
          returnObjects
        />
      )}
    </div>
  );
}
